import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import { Error } from '../design/elements';
import FormField from './formField';
import {
  GroupTitle,
  GroupWrapper,
  LocationWrapper,
  LocationGroupTitle,
  LocationButton,
  InlineRepeatedFormField
} from './css';
import {
  keyServiceAddresses,
  hasQuebecServiceAddress,
  fieldEmergencyPremiseContactNumber,
  fieldEmergencyExtension,
  fieldpreferredLanguageOfContact,
  fieldpreferredLanguageOfContactOne,
  fieldpreferredLanguageOfContactTwo,
  fieldEmergencyPremiseContactNumberNonRepeated,
  fieldEmergencyExtensionNonRepeated,
  winbackPrefferedInstallationNoteProvinces,
  statementOfWork,
  appendices
} from '../utils/constant';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import stringInterpolation from '../utils/stringInterpolation';
import request from '../utils/request';
import { indexToLetter } from '../utils/utils';

const RepeatedForm = ({
  data,
  formErrors,
  formFields,
  orderData,
  setFormFields,
  isEditing,
  isFormTouched,
  setIsFormTouched
}) => {
  const {
    name,
    key,
    repeatBasedOnBackendField,
    formFields: contentFulFields,
    formTitle
  } = data;
  const localizedStrings = useLocalizedStringsContext();
  const {
    formButtons: { addNewFormButtonLabel, deleteFormButtonLabel },
    modalForm: { fieldRequiredMessage }
  } = localizedStrings;
  let formsList = formFields[key];
  formsList =
    formsList && (formsList.length ? formsList : Object.values(formsList));
  const repeatedFormError = formErrors[key];
  const formObjectKeys = useMemo(
    () =>
      contentFulFields.reduce((acc, { fields: { name: formName, value } }) => {
        acc[formName] = value;
        return acc;
      }, {}),
    [contentFulFields]
  );

  const isCustomerLocation = key === keyServiceAddresses;
  const isAddEmergencyContact = key === 'emergencyContactDetails';
  const isStatementOfWork = key === statementOfWork;
  const isAppendices = key === appendices;

  const formListItemNumberingFormat = {
    [statementOfWork]: 'numbers',
    [appendices]: 'letters'
  };

  const fieldsMap = useMemo(
    () =>
      contentFulFields.reduce((acc, { fields }) => {
        acc[fields.name] = { ...fields };
        return acc;
      }, {}),
    [contentFulFields]
  );

  const hasQuebecSerAddress = () => {
    return (
      orderData.serviceAddress.includes(hasQuebecServiceAddress[0]) ||
      orderData.serviceAddress.includes(hasQuebecServiceAddress[1]) ||
      orderData.serviceAddress.includes(hasQuebecServiceAddress[2])
    );
  };

  const getOriginalFieldSequence = () => {
    return get(data, 'formFields', []).map(
      formFieldItem => formFieldItem.fields.name
    );
  };

  useEffect(() => {
    const province = request.getProvince();
    if (isAddEmergencyContact) {
      if (!hasQuebecSerAddress()) {
        Object.keys(fieldsMap).forEach(mkey => {
          if (mkey === fieldpreferredLanguageOfContact) {
            delete fieldsMap[mkey];
          }
          if (mkey === fieldpreferredLanguageOfContactOne) {
            delete fieldsMap[mkey];
          }
          if (mkey === fieldpreferredLanguageOfContactTwo) {
            delete fieldsMap[mkey];
          }
        });
        Object.keys(formsList[0]).forEach(mkey => {
          if (mkey === fieldpreferredLanguageOfContact) {
            delete formsList[0][mkey];
          }
          if (mkey === fieldpreferredLanguageOfContactOne) {
            delete formsList[0][mkey];
          }
          if (mkey === fieldpreferredLanguageOfContactTwo) {
            delete formsList[0][mkey];
          }
          // Hide Premise & Extension phone number fields if province is Alberta or British Columbia
          if (
            winbackPrefferedInstallationNoteProvinces.includes(province) &&
            (mkey === fieldEmergencyExtensionNonRepeated ||
              mkey === fieldEmergencyPremiseContactNumberNonRepeated)
          ) {
            delete formsList[0][mkey];
          }
        });
      }
    }
  }, []);

  useEffect(() => {
    if (isEditing && orderData && orderData.id && orderData[key]) {
      const updatedFormFields = { ...formFields };
      updatedFormFields[key] = [...orderData[key]];
      setFormFields(updatedFormFields);
      if (isFormTouched) {
        setIsFormTouched(false);
      }
    }
  }, [isEditing, orderData]);

  const addNewForm = () => {
    if (
      orderData[repeatBasedOnBackendField] > formsList.length ||
      isCustomerLocation ||
      isAddEmergencyContact ||
      isStatementOfWork ||
      isAppendices
    ) {
      if (isAddEmergencyContact && formsList.length === 5) {
        return;
      }
      const updatedFormFields = { ...formFields };
      updatedFormFields[key] = [...formsList, { ...formObjectKeys }];
      setFormFields(updatedFormFields);
    }
  };

  const removeFormGroup = index => {
    const fieldList = formsList;
    fieldList.splice(index, 1);
    const updatedFormFields = { ...formFields };
    updatedFormFields[key] = formsList;
    setFormFields(updatedFormFields);
  };

  const handleFormField = (index, fieldKey, value) => {
    const fieldList = formsList;
    fieldList[index][fieldKey] = value;
    const updatedFormFields = { ...formFields };
    updatedFormFields[key] = fieldList;
    if (!isFormTouched) {
      setIsFormTouched(true);
    }
    setFormFields(updatedFormFields);
  };

  const MainWrapper = isCustomerLocation ? LocationWrapper : GroupWrapper;
  const TitleWrapper = isCustomerLocation ? LocationGroupTitle : GroupTitle;

  const renderDeleteButton = index => {
    if (isCustomerLocation) {
      return (
        <LocationButton
          style={{ color: 'red', fontSize: '12px', marginLeft: '5em' }}
          onClick={() => {
            removeFormGroup(index);
          }}
        >
          {deleteFormButtonLabel}
        </LocationButton>
      );
    }

    return (
      <button
        type="button"
        style={{ float: 'right' }}
        className="frg-icon icon-minus"
        onClick={() => {
          removeFormGroup(index);
        }}
      />
    );
  };

  const renderAddButton = index => {
    if (isCustomerLocation) {
      return (
        <LocationButton onClick={() => addNewForm()}>
          » {stringInterpolation(addNewFormButtonLabel, formTitle)}
        </LocationButton>
      );
    }

    return (
      !isAddEmergencyContact && (
        <button
          type="submit"
          className={`frg-button ${
            index !== orderData[repeatBasedOnBackendField] - 1
              ? 'color-green'
              : 'state-disabled'
          }`}
          onClick={() => addNewForm()}
        >
          {stringInterpolation(addNewFormButtonLabel, formTitle)}
        </button>
      )
    );
  };

  const getSerialNumber = index => {
    const numberingFormat = formListItemNumberingFormat[key];
    switch (numberingFormat) {
      case 'letters':
        return indexToLetter(index);
      case 'numbers':
        return index + 1;
      default:
        return '';
    }
  };

  return (
    <MainWrapper>
      {!isCustomerLocation && !isAddEmergencyContact && (
        <TitleWrapper>{name}</TitleWrapper>
      )}
      {formsList &&
        formsList.length > 0 &&
        formsList.map((form, index) => {
          const repeatedFormTitle = `${formTitle} ${getSerialNumber(index)}`;
          let fieldsList = Object.keys(form);
          if (isStatementOfWork || isAppendices)
            fieldsList = getOriginalFieldSequence();
          return (
            <>
              <TitleWrapper>
                {repeatedFormTitle}
                {index !== 0 && renderDeleteButton(index)}
              </TitleWrapper>
              {fieldsList.map(formKey => {
                const fieldKey = `repeatedForm${key}${formKey}${index}`;
                const fieldFormKey = `${formKey}-${index}`;
                const currentValue = formsList[index][formKey];
                const fieldData = {
                  ...fieldsMap[formKey],
                  name: fieldFormKey,
                  sysId: fieldKey,
                  value: currentValue,
                  onChange: ({ target: { value } }) =>
                    handleFormField(index, formKey, value)
                };
                let error =
                  repeatedFormError &&
                  !currentValue &&
                  fieldsMap[formKey].required
                    ? fieldRequiredMessage
                    : '';
                if (error === '') {
                  if (repeatedFormError && currentValue) {
                    if (fieldData.fieldValidation) {
                      if (
                        fieldData.fieldValidation.fields.logic &&
                        fieldData.fieldValidation.fields.logic === 'regex'
                      ) {
                        const regex = new RegExp(
                          fieldData.fieldValidation.fields.value
                        );
                        if (!regex.test(currentValue)) {
                          error = fieldData.fieldValidation.fields.errorMessage;
                        }
                        if (currentValue.length > fieldData.maxLength) {
                          error = fieldData.fieldValidation.fields.errorMessage;
                        }
                      }
                    }
                  }
                }
                return (
                  <Fragment key={fieldKey}>
                    {fieldKey === fieldEmergencyPremiseContactNumber ||
                    fieldKey === fieldEmergencyExtension ? (
                      <InlineRepeatedFormField>
                        <FormField
                          key={`field${formKey}${fieldKey}`}
                          data={fieldData}
                        />
                        {error && <Error text={error} />}
                      </InlineRepeatedFormField>
                    ) : (
                      <div
                        style={
                          isCustomerLocation ? { paddingLeft: '1rem' } : {}
                        }
                      >
                        <FormField
                          key={`field${formKey}${fieldKey}`}
                          data={fieldData}
                        />
                        {error && <Error text={error} />}
                      </div>
                    )}
                  </Fragment>
                );
              })}
              {index === formsList.length - 1 && renderAddButton(index)}
            </>
          );
        })}
    </MainWrapper>
  );
};

RepeatedForm.propTypes = {
  data: PropTypes.shape({
    key: PropTypes.string.isRequired,
    formFields: PropTypes.arrayOf(PropTypes.object),
    name: PropTypes.string.isRequired,
    repeatBasedOnBackendField: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    formTitle: PropTypes.string
  }).isRequired,
  formErrors: PropTypes.objectOf(PropTypes.object),
  formFields: PropTypes.objectOf(PropTypes.object).isRequired,
  orderData: PropTypes.objectOf(PropTypes.object).isRequired,
  setFormFields: PropTypes.func.isRequired,
  isEditing: PropTypes.string.isRequired,
  isFormTouched: PropTypes.bool.isRequired,
  setIsFormTouched: PropTypes.func.isRequired
};

RepeatedForm.defaultProps = {
  formErrors: {}
};

export default RepeatedForm;
